
import Themeable from 'vuetify/lib/mixins/themeable';
import { Component, Vue, Watch } from 'vue-property-decorator';
import { DataTableHeader } from 'vuetify';
import DNSRepositoryInterface, { DNSZonesInterface } from '@/services/DNS/Interfaces';
import DeleteDnsZoneDialog from '@/components/dialogs/dns/DeleteDnsZoneDialog.vue';
import SyncDNSDialog from '@/components/dialogs/dns/SyncDNSDialog.vue';
import { ServerInterface, ServerRepositoryInterface } from '@/services/Server/Interfaces';
import SyncServerDNSDialog from '@/components/dialogs/dns/SyncServerDNSDialog.vue';

@Component({
  mixins: [Themeable],
  inject: [
    'dnsRepository',
    'serverRepository',
  ],
  components: {
    DeleteDnsZoneDialog,
    SyncDNSDialog,
    SyncServerDNSDialog,
  },
})
export default class AllDns extends Vue {
  public loading: boolean = false;

  public headers: DataTableHeader[] = [
    { text: 'Website', value: 'websiteId' },
    { text: 'Domain', value: 'installation.domain' },
    { text: 'Server', value: 'installation.server' },
    { text: 'Server IP', value: 'installation.dnsIP' },
    { text: 'Status', value: 'status' },
    { text: 'Actions', value: 'actions', sortable: false },
  ];

  public tableSearch: string = '';
  public zoneSearch: string = '';

  public options: { page: number, itemsPerPage: number } = {
    page: 1,
    itemsPerPage: 10,
  };

  public totalZonesLength = 0;

  public readonly dnsRepository!: DNSRepositoryInterface;

  public dnsZones: DNSZonesInterface[] = [];
  public deleteDnsZoneDialogData: DNSZonesInterface | false = false;
  public deleteDnsZoneDialog: string = '';

  public syncDNSDialog: string = '';

  public syncServerDNSDialog: string = '';
  public serversForSyncDialog: ServerInterface[] = [];

  public readonly serverRepository!: ServerRepositoryInterface;
  public serversMap: { [id: string]: string } = {};

  @Watch('zoneSearch')
  public watchSearch(): void {
    this.options.page = 1;
  }

  public async created(): Promise<void> {
    this.loading = true;
    try {
      await this.search();
    } catch (err) {
      this.$errorDialog.show('An issue occurred while retrieving websites.');
    } finally {
      this.loading = false;
    }
  }

  public async search(ignoreErrors: boolean = false): Promise<void> {
    if (this.zoneSearch.length > 0 && this.zoneSearch.length < 3) {
      this.$errorDialog.show('Please be more specific about your search!');
      return;
    }

    this.loading = true;
    try {
      [this.dnsZones, this.totalZonesLength] = await this.dnsRepository.getAllZones(this.zoneSearch, this.options.page, this.options.itemsPerPage);
      if (this.dnsZones.length) {
        const servers = await this.serverRepository.getServersByIds(
            this.dnsZones.filter(zone => !!zone.installation).map((zone) => zone.installation.server),
        );

        servers.forEach((server) => {
          this.serversMap[server._id] = server.hostname;
        });
      }
      this.loading = false;
    } catch (e) {
      this.dnsZones = [];

      if (!ignoreErrors) {
        if (e.response.data) {
          this.$errorDialog.show(e.response.data.message);
        } else {
          this.$errorDialog.show(e.message);
        }
      }
    }
  }

  public createZone(): void {
    this.$router.push({ name: 'dns_zone_create' });
  }

  public editZone(): void {
    console.log('Edit DNS zone');
  }

  public async deleteZonePrompt(zone: DNSZonesInterface): Promise<void> {
    this.deleteDnsZoneDialog = 'delete-dns-zone-dialog';
    this.deleteDnsZoneDialogData = zone;
  }

  public async deleteZone(zone: DNSZonesInterface): Promise<void> {
    try {
      await this.dnsRepository.deleteDNSZone(zone.id);

      this.$successDialog.show(`DNS Zone has been removed.`);
      await this.search(true);
    } catch (e) {
      this.$errorDialog.show(`Error deleting dns zone: ${e.message}`);
    }
  }

  public async showSyncDialog(): Promise<void> {
    this.syncDNSDialog = 'SyncDNSDialog';
  }

  public async syncDNS(step: number): Promise<void> {
    try {
      await this.dnsRepository.syncDNS(undefined, undefined, step);
    } catch (err) {
      this.$errorDialog.show(err?.response?.data?.message || err.message);
    }
  }

  public async showSyncServerDialog(): Promise<void> {
    try {
      this.serversForSyncDialog = await this.serverRepository.getAllServers();
      this.syncServerDNSDialog = 'SyncServerDNSDialog';
    } catch (err) {
      this.$errorDialog.show('Failed to load servers for sync dialog!');
    }
  }

  public async syncServerDNS(server: string, step: number): Promise<void> {
    try {
      await this.dnsRepository.syncDNS(undefined, server, step);
    } catch (err) {
      this.$errorDialog.show(err?.response?.data?.message || err.message);
    }
  }
}
